import React, { useEffect } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { useWebsiteData } from '../hooks/useWebsiteData';
import { WebsiteLayout } from '../components/WebsiteLayout';
import { RenderedWebsite } from '../components/RenderedWebsite';
import { isCustomDomain as checkIsCustomDomain } from '../utils/routeHandler';

export const WebsiteViewer: React.FC = () => {
  const { subdomain, slug } = useParams<{ subdomain: string; slug?: string }>();
  const location = useLocation();

  // Determine if we're on a custom domain
  const isCustomDomain = checkIsCustomDomain();

  // Extract identifier (subdomain or custom domain)
  const identifier =
    subdomain ||
    (() => {
      const hostname = window.location.hostname;

      // If it's a custom domain, use the full hostname
      if (isCustomDomain) {
        console.log('WebsiteViewer custom domain detected:', hostname);
        return hostname;
      }

      // Otherwise, extract subdomain
      const parts = hostname.split('.');

      console.log('WebsiteViewer subdomain extraction:', {
        hostname,
        parts,
        length: parts.length,
        firstPart: parts[0],
      });

      // For local development: subdomain.compete-pages-local.com
      // For production: subdomain.competepages.com
      if (parts.length >= 3) {
        const extractedSubdomain = parts[0];
        console.log('Extracted subdomain:', extractedSubdomain);
        return extractedSubdomain;
      }
      console.log('No subdomain extracted, returning empty string');
      return '';
    })();

  const currentSlug = slug || location.pathname.slice(1) || '';

  const { website, page, components, navigation, theme, layout, loading, error, showBranding } =
    useWebsiteData(identifier, currentSlug, isCustomDomain);

  // Debug logging - detailed component analysis
  useEffect(() => {
    if (website && page) {
      console.log('🔍 WebsiteViewer Detailed Debug:', {
        website: website.name,
        page: page.title,
        slug: page.slug,
        isHomePage: page.isHomePage,
        componentsCount: components.length,
        componentsDetailed: components.map(c => ({
          id: c.id,
          type: c.type,
          order: c.order,
          hasData: !!c.data,
        })),
        rawComponents: components,
        navigation: navigation.length,
        pageComponents: page.components, // Raw components string from page
      });
    }
  }, [website, page, components, navigation]);

  // Note: Document title and meta tags are now handled by HeadContentComponent
  // which uses React Helmet for proper meta tag management

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading website...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error || !website) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-4">
          <div className="mb-6">
            <svg
              className="mx-auto h-16 w-16 text-gray-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 15.5c-.77.833.192 2.5 1.732 2.5z"
              />
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Website Not Found</h1>
          <p className="text-gray-600 mb-6">
            {error || `The website "${identifier}" could not be found or is not published.`}
          </p>
          <div className="space-y-2 text-sm text-gray-500">
            <p>
              {isCustomDomain ? 'Domain' : 'Subdomain'}:{' '}
              <span className="font-mono">{identifier}</span>
            </p>
            <p>
              Page: <span className="font-mono">{currentSlug || 'home'}</span>
            </p>
          </div>
        </div>
      </div>
    );
  }

  // No page found
  if (!page) {
    return (
      <WebsiteLayout
        website={website}
        navigation={navigation}
        theme={theme}
        showBranding={showBranding}
      >
        <div className="min-h-96 flex items-center justify-center">
          <div className="text-center max-w-md mx-auto px-4">
            <h1 className="text-2xl font-bold text-gray-900 mb-4">Page Not Found</h1>
            <p className="text-gray-600 mb-6">
              The page "{currentSlug}" could not be found on this website.
            </p>
            <a
              href="/"
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 transition-colors"
            >
              Go to Homepage
            </a>
          </div>
        </div>
      </WebsiteLayout>
    );
  }

  return (
    <RenderedWebsite
      website={website}
      page={page}
      components={components}
      navigation={navigation}
      theme={theme}
      layout={layout}
      showBranding={showBranding}
      identifier={identifier}
    />
  );
};
